import React, { useContext, useState } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../shared/Context/auth-context';
import PageHeading from '../shared/components/UIElements/PageHeading';
import Icon from '../shared/components/UIElements/Icon';
import { useSessionState } from '../shared/hooks/session-state-hook';
import { sessionKey } from '../shared/util/session-storage';
import { useCareMutation, useCareResource } from './care-api';
import '../shared/components/FormElements/Button.css';
import './Care.css';
import './CareTeam.css';

const scopes = [
  ['timeline', 'Clinical history', 'Consultations and recorded events'],
  ['vitals', 'Vitals', 'Measurements and trends'],
  ['reports', 'Reports', 'Uploaded reports'],
  ['updates', 'Updates and conversations', 'Symptoms, questions, and replies'],
  ['medications', 'Medications', 'Prescription history and pharmacy notes']
];
const scopeNames = Object.fromEntries(scopes.map(([key, label]) => [key, label]));
const blankDraft = {
  role: 'doctor',
  query: '',
  search: '',
  selectedId: '',
  primary: false,
  scopes: ['reports', 'updates'],
  expiresAt: ''
};
const dateLabel = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown date' : date.toLocaleDateString();
};
const isExpired = (member) => Boolean(member.expiresAt && new Date(member.expiresAt) <= new Date());
const isActive = (member) => member.status === 'accepted' && !isExpired(member);

const CareTeam = () => {
  const auth = useContext(AuthContext);
  const patientId = auth.patientId;
  const recordPath = patientId ? `/patients/${encodeURIComponent(patientId)}` : null;
  const record = useCareResource(recordPath);
  const mutation = useCareMutation();
  const [notice, setNotice] = useState('');
  const [validation, setValidation] = useState('');
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [draft, setDraft, clearDraft, stored] = useSessionState(
    sessionKey(`patient:${patientId}`, 'care:team:invite'),
    blankDraft,
    {
      restore: (value, fallback) => {
        if (!value || typeof value !== 'object') return fallback;
        return {
          ...fallback,
          role: value.role === 'pharmacy' ? 'pharmacy' : 'doctor',
          query: typeof value.query === 'string' ? value.query : '',
          search: typeof value.search === 'string' ? value.search : '',
          selectedId: typeof value.selectedId === 'string' ? value.selectedId : '',
          primary: value.role !== 'pharmacy' && value.primary === true,
          scopes: Array.isArray(value.scopes)
            ? value.scopes.filter((scope) => scopeNames[scope])
            : fallback.scopes,
          expiresAt: typeof value.expiresAt === 'string' ? value.expiresAt : ''
        };
      }
    }
  );
  const directory = useCareResource(
    patientId && draft.search.trim().length >= 2
      ? `/directory?role=${draft.role}&q=${encodeURIComponent(draft.search.trim())}`
      : null
  );
  const members = Array.isArray(record.data?.members) ? record.data.members : [];
  const people = Array.isArray(directory.data?.people) ? directory.data.people : [];
  const selectedPerson = people.find((person) => person.id === draft.selectedId);
  const activeMembers = members.filter(isActive);
  const primary = activeMembers.find(
    (member) =>
      member.role === 'doctor' &&
      (member.personId === record.data?.primaryDoctorId || member.primary)
  );
  const pending = members.filter((member) => member.status === 'pending' && !isExpired(member));
  const previous = members.filter((member) => !isActive(member) && !pending.includes(member));
  const outstanding = (record.data?.entries || []).filter(
    (entry) =>
      ['pending', 'needs-information'].includes(entry.status) &&
      entry.reviewerId &&
      !activeMembers.some(
        (member) => member.role === 'doctor' && member.personId === entry.reviewerId
      )
  );
  const patchDraft = (patch) => {
    setDraft((value) => ({ ...value, ...patch }));
    setValidation('');
    setNotice('');
  };
  const search = (event) => {
    event.preventDefault();
    if (draft.query.trim().length < 2) {
      setValidation('Enter at least two characters to search.');
      return;
    }
    if (draft.query.trim() === draft.search) directory.reload();
    patchDraft({ search: draft.query.trim(), selectedId: '' });
  };
  const invite = async (event) => {
    event.preventDefault();
    mutation.clearError();
    setNotice('');
    if (!selectedPerson)
      return setValidation('Choose a doctor or pharmacy from the search results.');
    const sharedScopes = draft.primary ? scopes.map(([key]) => key) : draft.scopes;
    if (!sharedScopes.length) return setValidation('Select at least one category to share.');
    const expiry = draft.expiresAt ? new Date(`${draft.expiresAt}T23:59:59`) : null;
    if (expiry && (Number.isNaN(expiry.getTime()) || expiry <= new Date())) {
      return setValidation('Choose an access expiry date that has not passed.');
    }
    try {
      await mutation.mutate(`${recordPath}/members`, 'POST', {
        personId: selectedPerson.id,
        role: draft.role,
        primary: draft.role === 'doctor' && draft.primary,
        scopes: sharedScopes,
        expiresAt: expiry ? expiry.toISOString() : null
      });
      setNotice(`Request sent to ${selectedPerson.name}. Access starts when they accept.`);
      setValidation('');
      clearDraft();
      await record.reload();
    } catch {
      /* The API hook exposes the persisted request error. */
    }
  };
  const updateMember = async (member, action) => {
    mutation.clearError();
    setNotice('');
    try {
      await mutation.mutate(`${recordPath}/members/${encodeURIComponent(member.id)}`, 'PATCH', {
        action
      });
      setNotice(
        action === 'make-primary'
          ? `${member.name} is now your primary reviewer. Existing review assignments remain unchanged.`
          : action === 'remove-primary'
            ? 'Primary reviewer removed. Choose a reviewer when sending your next update.'
            : `Access removed for ${member.name}. Outstanding reviews have not been transferred.`
      );
      setConfirmRevoke(null);
      await record.reload();
    } catch {
      /* Keep the current list until the server confirms a change. */
    }
  };
  const memberCard = (member) => {
    const active = isActive(member);
    const pendingMember = member.status === 'pending' && !isExpired(member);
    const primaryMember = active && member.role === 'doctor' && primary?.id === member.id;
    const status =
      isExpired(member) && ['accepted', 'pending'].includes(member.status)
        ? 'Expired'
        : primaryMember
          ? 'Primary reviewer'
          : member.status === 'accepted'
            ? 'Shared access'
            : member.status;
    return (
      <li className="care-team-member" key={member.id}>
        <div className="care-team-member__heading">
          <span className="care-team-avatar">
            <Icon name={member.role === 'pharmacy' ? 'pharmacy' : 'doctor'} size={22} />
          </span>
          <div>
            <h3>{member.name}</h3>
            <p className="care-muted">
              {member.role === 'pharmacy' ? 'Pharmacy' : member.specialization || 'Doctor'}
            </p>
          </div>
          <span className="care-pill">{status}</span>
        </div>
        {pendingMember && member.primary && (
          <p className="care-team-primary-note">
            Primary reviewer requested. Your current reviewer stays in place until acceptance.
          </p>
        )}
        <p className="care-team-access">
          <strong>Shared:</strong>{' '}
          {(member.scopes || []).map((scope) => scopeNames[scope] || scope).join(', ') ||
            'No categories'}
        </p>
        <p className="care-muted">
          {member.expiresAt ? `Access expires ${dateLabel(member.expiresAt)}` : 'No expiry date'}
        </p>
        {active && member.role === 'doctor' && (
          <div className="care-actions">
            <button
              type="button"
              className="button button--inverse button--small"
              disabled={
                mutation.saving ||
                record.loading ||
                (!primaryMember && scopes.some(([scope]) => !member.scopes.includes(scope)))
              }
              onClick={() =>
                updateMember(member, primaryMember ? 'remove-primary' : 'make-primary')
              }
            >
              {primaryMember ? 'Remove primary role' : 'Make primary reviewer'}
            </button>
            {!primaryMember && (
              <span className="care-muted">
                {scopes.every(([scope]) => member.scopes.includes(scope))
                  ? 'Existing review assignments stay with their current reviewer.'
                  : 'To make this doctor primary, remove this limited access and invite them as a primary reviewer with all five categories.'}
              </span>
            )}
          </div>
        )}
        {(active || pendingMember) &&
          (confirmRevoke === member.id ? (
            <div className="care-team-confirm">
              <p>
                Remove {member.name}’s access? Pending reviews stay assigned until you reassign
                them.
              </p>
              <div className="care-actions">
                <button
                  type="button"
                  className="button button--danger button--small"
                  disabled={mutation.saving}
                  onClick={() => updateMember(member, 'revoke')}
                >
                  Confirm removal
                </button>
                <button
                  type="button"
                  className="button button--inverse button--small"
                  disabled={mutation.saving}
                  onClick={() => setConfirmRevoke(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="care-team-remove"
              disabled={mutation.saving || record.loading}
              onClick={() => setConfirmRevoke(member.id)}
            >
              {pendingMember ? 'Cancel request' : 'Remove access'}
            </button>
          ))}
      </li>
    );
  };

  if (!patientId)
    return (
      <div className="care-error" role="alert">
        Sign in as a patient to manage your care team.
      </div>
    );
  return (
    <div className="care-team">
      <PageHeading
        eyebrow="CARE, CONNECTED"
        title="My care team"
        description="Choose who sees your history and who reviews your updates."
      >
        <Link className="button button--inverse" to="/care">
          My health record <Icon name="arrow" size={17} />
        </Link>
        <button
          className="button button--inverse"
          type="button"
          disabled={record.loading || mutation.saving}
          onClick={record.reload}
        >
          Refresh
        </button>
      </PageHeading>
      {notice && (
        <div role="status" className="care-notice">
          {notice}
        </div>
      )}
      {mutation.error && (
        <div role="alert" className="care-error">
          {mutation.error}
        </div>
      )}
      {record.error && (
        <div role="alert" className="care-error">
          <p>{record.error}</p>
          <button className="button button--inverse" onClick={record.reload} type="button">
            Retry loading care team
          </button>
        </div>
      )}
      {record.loading && (
        <p role="status" className="care-muted">
          Loading your care team…
        </p>
      )}
      {record.data && !record.error && (
        <>
          <section className="care-team-reviewer" aria-labelledby="primary-reviewer-title">
            <span className="care-team-avatar">
              <Icon name="doctor" size={25} />
            </span>
            <div>
              <p className="eyebrow">YOUR PRIMARY REVIEWER</p>
              <h2 id="primary-reviewer-title">
                {primary ? primary.name : 'Your choice, whenever you need it'}
              </h2>
              <p>
                {primary
                  ? 'New updates can be sent to this doctor. You can choose a different reviewer for each update.'
                  : 'A primary doctor is optional. Without one, choose a doctor when you send an update for review.'}
              </p>
              <p className="care-muted">
                Sharing access alone does not assign a doctor to review your updates. Existing
                assignments stay with their named reviewer when you change your team.
              </p>
            </div>
          </section>
          {outstanding.length > 0 && (
            <div className="care-notice">
              {outstanding.length} outstanding review{outstanding.length === 1 ? ' has' : 's have'}{' '}
              a reviewer whose access is no longer active.{' '}
              <Link to="/care">Open your record to choose a new reviewer.</Link>
            </div>
          )}
          <div className="care-team-layout">
            <section className="care-panel care-team-roster" aria-labelledby="shared-team-title">
              <h2 id="shared-team-title">People in your care</h2>
              {!activeMembers.length && !pending.length && (
                <div className="care-empty">
                  <Icon name="patient" size={30} />
                  <h3>Your team starts here</h3>
                  <p>Find a doctor or pharmacy and choose the information you want to share.</p>
                </div>
              )}
              {activeMembers.length > 0 && (
                <ul className="care-team-list">{activeMembers.map(memberCard)}</ul>
              )}
              {pending.length > 0 && (
                <>
                  <h3 className="care-team-section-title">
                    Awaiting acceptance · {pending.length}
                  </h3>
                  <ul className="care-team-list">{pending.map(memberCard)}</ul>
                </>
              )}
              {previous.length > 0 && (
                <details className="care-team-previous">
                  <summary>Previous access and requests ({previous.length})</summary>
                  <ul className="care-team-list">{previous.map(memberCard)}</ul>
                </details>
              )}
            </section>
            <section className="care-panel care-team-invite" aria-labelledby="invite-team-title">
              <h2 id="invite-team-title">Add to your care team</h2>
              <p className="care-muted">Choose a person, set their access, and send a request.</p>
              <form onSubmit={search} className="care-team-search">
                <label htmlFor="care-team-role">Find a</label>
                <select
                  id="care-team-role"
                  value={draft.role}
                  disabled={mutation.saving}
                  onChange={(event) =>
                    patchDraft({
                      role: event.target.value,
                      selectedId: '',
                      search: '',
                      primary: false,
                      scopes:
                        event.target.value === 'pharmacy' ? ['medications'] : ['reports', 'updates']
                    })
                  }
                >
                  <option value="doctor">Doctor</option>
                  <option value="pharmacy">Pharmacy</option>
                </select>
                <label htmlFor="care-team-search">Search by name</label>
                <div className="care-team-search-row">
                  <input
                    id="care-team-search"
                    type="search"
                    value={draft.query}
                    maxLength={100}
                    placeholder={draft.role === 'doctor' ? 'Doctor’s name' : 'Pharmacy name'}
                    disabled={mutation.saving}
                    onChange={(event) => patchDraft({ query: event.target.value })}
                  />
                  <button
                    type="submit"
                    className="button"
                    disabled={draft.query.trim().length < 2 || mutation.saving || directory.loading}
                  >
                    Search
                  </button>
                </div>
              </form>
              {directory.loading && (
                <p role="status" className="care-muted">
                  Searching…
                </p>
              )}
              {directory.error && (
                <div role="alert" className="care-error">
                  <p>{directory.error}</p>
                  <button
                    type="button"
                    className="button button--inverse"
                    onClick={directory.reload}
                  >
                    Retry search
                  </button>
                </div>
              )}
              {!directory.loading &&
                !directory.error &&
                draft.search &&
                directory.data &&
                !people.length && (
                  <p role="status" className="care-muted">
                    No matching {draft.role === 'doctor' ? 'doctors' : 'pharmacies'}. Try a
                    different name.
                  </p>
                )}
              {people.length > 0 && !directory.error && !directory.loading && (
                <fieldset className="care-team-results">
                  <legend>Choose a {draft.role === 'doctor' ? 'doctor' : 'pharmacy'}</legend>
                  {people.map((person) => {
                    const existing = members.some(
                      (member) =>
                        member.personId === person.id &&
                        member.role === draft.role &&
                        (isActive(member) || (member.status === 'pending' && !isExpired(member)))
                    );
                    return (
                      <label
                        key={person.id}
                        className={`care-team-result ${draft.selectedId === person.id ? 'care-team-result--selected' : ''}`}
                      >
                        <input
                          type="radio"
                          name="care-team-person"
                          value={person.id}
                          checked={draft.selectedId === person.id}
                          disabled={existing || mutation.saving}
                          onChange={() => patchDraft({ selectedId: person.id })}
                        />
                        <span>
                          <strong>{person.name}</strong>
                          <span className="care-muted">
                            {existing
                              ? 'Already invited or in your team'
                              : person.specialization ||
                                (person.role === 'pharmacy' ? 'Pharmacy' : 'Doctor')}
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </fieldset>
              )}
              {selectedPerson && !directory.loading && !directory.error && (
                <form onSubmit={invite} className="care-team-access-form">
                  <h3>Share with {selectedPerson.name}</h3>
                  {draft.role === 'doctor' && (
                    <label className="care-team-checkbox care-team-primary-choice">
                      <input
                        type="checkbox"
                        checked={draft.primary}
                        disabled={mutation.saving}
                        onChange={(event) =>
                          patchDraft({
                            primary: event.target.checked,
                            scopes: event.target.checked ? scopes.map(([key]) => key) : draft.scopes
                          })
                        }
                      />
                      <span>
                        <strong>Request as my primary reviewer</strong>
                        <span className="care-muted">
                          Shares all five categories. Your current primary reviewer changes only
                          after this doctor accepts.
                        </span>
                      </span>
                    </label>
                  )}
                  <fieldset className="care-team-scopes">
                    <legend>Information to share</legend>
                    {scopes.map(([key, label, hint]) => (
                      <label key={key} className="care-team-checkbox">
                        <input
                          type="checkbox"
                          checked={draft.primary || draft.scopes.includes(key)}
                          disabled={draft.primary || mutation.saving}
                          onChange={(event) =>
                            patchDraft({
                              scopes: event.target.checked
                                ? [...draft.scopes, key]
                                : draft.scopes.filter((scope) => scope !== key)
                            })
                          }
                        />
                        <span>
                          <strong>{label}</strong>
                          <span className="care-muted">{hint}</span>
                        </span>
                      </label>
                    ))}
                  </fieldset>
                  <label htmlFor="care-team-expiry">
                    Access expiry date <span className="care-muted">(optional)</span>
                  </label>
                  <input
                    id="care-team-expiry"
                    type="date"
                    value={draft.expiresAt}
                    disabled={mutation.saving}
                    onChange={(event) => patchDraft({ expiresAt: event.target.value })}
                  />
                  <p className="care-muted care-team-expiry-hint">
                    Leave blank for ongoing access. You can remove access later.
                  </p>
                  <div className="care-actions">
                    <button
                      type="submit"
                      className="button"
                      disabled={mutation.saving || record.loading}
                    >
                      {mutation.saving ? 'Sending request…' : 'Send sharing request'}
                    </button>
                    <button
                      type="button"
                      className="button button--inverse"
                      disabled={mutation.saving}
                      onClick={() => patchDraft({ selectedId: '' })}
                    >
                      Back to results
                    </button>
                  </div>
                </form>
              )}
              {validation && (
                <p role="alert" className="care-error">
                  {validation}
                </p>
              )}
              {!stored && (
                <p role="status" className="care-notice">
                  Your browser could not remember this draft. Keep this page open until you send the
                  request.
                </p>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
};

export default CareTeam;
